import { supabase } from './supabase.js';

export const messagesService = {
  // Message CRUD operations
  async getMessages(filters = {}) {
    console.log('📬 Getting messages with filters:', filters);
    
    try {
      let query = supabase
        .from('family_messages')
        .select(`
          *,
          sender:family_members!sender_id(id, name, avatar, role),
          recipients:message_recipients(
            *,
            recipient:family_members!recipient_id(id, name, avatar, role)
          ),
          attachments:message_attachments(*)
        `)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.type) {
        query = query.eq('type', filters.type);
      }
      if (filters.priority) {
        query = query.eq('priority', filters.priority);
      }
      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      if (filters.senderId) {
        query = query.eq('sender_id', filters.senderId);
      }
      if (filters.recipientId) {
        // Get message IDs for this recipient
        const { data: recipientMessages } = await supabase
          .from('message_recipients')
          .select('message_id')
          .eq('recipient_id', filters.recipientId);
        
        const messageIds = recipientMessages?.map(r => r.message_id) || [];
        if (messageIds.length > 0) {
          query = query.in('id', messageIds);
        } else {
          // No messages for this recipient, return empty
          return [];
        }
      }
      if (filters.unreadOnly) {
        // Get unread message IDs
        const { data: unreadMessages } = await supabase
          .from('message_recipients')
          .select('message_id')
          .eq('status', 'unread');
        
        const messageIds = unreadMessages?.map(r => r.message_id) || [];
        if (messageIds.length > 0) {
          query = query.in('id', messageIds);
        } else {
          // No unread messages, return empty
          return [];
        }
      }
      if (filters.parentMessageId) {
        query = query.eq('parent_message_id', filters.parentMessageId);
      } else if (filters.threadsOnly) {
        query = query.is('parent_message_id', null);
      }

      // Sort by priority and date
      query = query
        .order('priority', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      console.log('✅ Messages retrieved:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('❌ Error getting messages:', error);
      throw error;
    }
  },

  async getMessage(messageId, currentUserId = null) {
    console.log('📨 Getting message:', messageId);
    
    try {
      const { data, error } = await supabase
        .from('family_messages')
        .select(`
          *,
          sender:family_members!sender_id(id, name, avatar, role),
          recipients:message_recipients(
            *,
            recipient:family_members!recipient_id(id, name, avatar, role)
          ),
          attachments:message_attachments(*)
        `)
        .eq('id', messageId)
        .single();

      if (error) throw error;

      // Auto-mark as read if currentUserId is provided
      if (currentUserId && data) {
        await this.markAsRead(messageId, currentUserId);
      }

      console.log('✅ Message retrieved:', data?.id);
      return data;
    } catch (error) {
      console.error('❌ Error getting message:', error);
      throw error;
    }
  },

  async sendMessage(messageData, senderId) {
    console.log('📤 Sending message:', { messageData, senderId });
    
    try {
      // Get sender details
      const { data: sender, error: senderError } = await supabase
        .from('family_members')
        .select('name, role')
        .eq('id', senderId)
        .single();

      if (senderError) throw senderError;

      // Validate message type based on sender role
      this._validateMessageType(messageData.type, sender.role);

      // Use the database function for sending messages
      const { data: messageId, error } = await supabase.rpc('send_family_message', {
        p_sender_id: senderId,
        p_type: messageData.type,
        p_subject: messageData.subject || null,
        p_content: messageData.content,
        p_recipient_ids: messageData.recipientIds || [],
        p_priority: messageData.priority || 'normal',
        p_expires_at: messageData.expiresAt || null,
        p_related_entity_type: messageData.relatedEntityType || null,
        p_related_entity_id: messageData.relatedEntityId || null,
        p_due_date: messageData.dueDate || null
      });

      if (error) throw error;

      // Handle attachments if provided
      if (messageData.attachments && messageData.attachments.length > 0) {
        await this._addAttachments(messageId, messageData.attachments, senderId);
      }

      // Get the complete message with relations
      const completeMessage = await this.getMessage(messageId);

      console.log('✅ Message sent:', messageId);
      return completeMessage;
    } catch (error) {
      console.error('❌ Error sending message:', error);
      throw error;
    }
  },

  async replyToMessage(parentMessageId, replyData, senderId) {
    console.log('↩️ Replying to message:', parentMessageId);
    
    try {
      // Get parent message to inherit family and recipients
      const parentMessage = await this.getMessage(parentMessageId);
      
      if (!parentMessage) {
        throw new Error('Parent message not found');
      }

      // Create reply with parent_message_id set
      const replyMessage = await this.sendMessage({
        ...replyData,
        type: replyData.type || 'reply',
        subject: replyData.subject || `Re: ${parentMessage.subject || 'Message'}`,
        recipientIds: replyData.recipientIds || [parentMessage.sender_id],
        parentMessageId: parentMessageId,
        relatedEntityType: parentMessage.related_entity_type,
        relatedEntityId: parentMessage.related_entity_id
      }, senderId);

      // Update parent message to reflect it has replies
      await supabase
        .from('family_messages')
        .update({ 
          has_replies: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', parentMessageId);

      console.log('✅ Reply sent:', replyMessage.id);
      return replyMessage;
    } catch (error) {
      console.error('❌ Error replying to message:', error);
      throw error;
    }
  },

  async updateMessage(messageId, updates, updaterId) {
    console.log('✏️ Updating message:', messageId);
    
    try {
      const updateData = {
        updated_at: new Date().toISOString()
      };

      if (updates.subject !== undefined) updateData.subject = updates.subject;
      if (updates.content !== undefined) updateData.content = updates.content;
      if (updates.priority !== undefined) updateData.priority = updates.priority;
      if (updates.status !== undefined) updateData.status = updates.status;
      if (updates.expiresAt !== undefined) updateData.expires_at = updates.expiresAt;

      const { data, error } = await supabase
        .from('family_messages')
        .update(updateData)
        .eq('id', messageId)
        .eq('sender_id', updaterId) // Only sender can update their message
        .select()
        .single();

      if (error) throw error;
      if (!data) throw new Error('Message not found or unauthorized');

      console.log('✅ Message updated:', messageId);
      return data;
    } catch (error) {
      console.error('❌ Error updating message:', error);
      throw error;
    }
  },

  async deleteMessage(messageId, deleterId) {
    console.log('🗑️ Deleting message:', messageId);
    
    try {
      const { error } = await supabase
        .from('family_messages')
        .delete()
        .eq('id', messageId)
        .eq('sender_id', deleterId); // Only sender can delete their message

      if (error) throw error;

      console.log('✅ Message deleted:', messageId);
      return { success: true };
    } catch (error) {
      console.error('❌ Error deleting message:', error);
      throw error;
    }
  },

  // Message status operations
  async markAsRead(messageId, readerId) {
    console.log('👁️ Marking message as read:', messageId, 'by:', readerId);
    
    try {
      const { data, error } = await supabase.rpc('mark_message_read', {
        p_message_id: messageId,
        p_reader_id: readerId
      });

      if (error) throw error;

      console.log('✅ Message marked as read:', messageId);
      return data;
    } catch (error) {
      console.error('❌ Error marking message as read:', error);
      throw error;
    }
  },

  async markAsAcknowledged(messageId, acknowledgerId) {
    console.log('✅ Acknowledging message:', messageId);
    
    try {
      // Update recipient status
      const { error: recipientError } = await supabase
        .from('message_recipients')
        .update({
          status: 'acknowledged',
          acknowledged_at: new Date().toISOString()
        })
        .eq('message_id', messageId)
        .eq('recipient_id', acknowledgerId);

      if (recipientError) throw recipientError;

      // Check if all recipients have acknowledged
      const { data: unacknowledged } = await supabase
        .from('message_recipients')
        .select('id')
        .eq('message_id', messageId)
        .neq('status', 'acknowledged');

      if (!unacknowledged || unacknowledged.length === 0) {
        // All recipients acknowledged, update main message
        await supabase
          .from('family_messages')
          .update({
            status: 'acknowledged',
            acknowledged_at: new Date().toISOString(),
            acknowledged_by: acknowledgerId
          })
          .eq('id', messageId);
      }

      console.log('✅ Message acknowledged:', messageId);
      return { success: true };
    } catch (error) {
      console.error('❌ Error acknowledging message:', error);
      throw error;
    }
  },

  async markAsCompleted(messageId, completerId) {
    console.log('🎯 Marking message as completed:', messageId);
    
    try {
      const { data, error } = await supabase
        .from('family_messages')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          completed_by: completerId
        })
        .eq('id', messageId)
        .select()
        .single();

      if (error) throw error;

      // Update all recipients to completed status
      await supabase
        .from('message_recipients')
        .update({
          status: 'completed',
          acknowledged_at: new Date().toISOString()
        })
        .eq('message_id', messageId);

      console.log('✅ Message marked as completed:', messageId);
      return data;
    } catch (error) {
      console.error('❌ Error marking message as completed:', error);
      throw error;
    }
  },

  async archiveMessage(messageId, archiverId) {
    console.log('📦 Archiving message:', messageId);
    
    try {
      const { data, error } = await supabase
        .from('family_messages')
        .update({
          status: 'archived',
          archived_at: new Date().toISOString(),
          archived_by: archiverId
        })
        .eq('id', messageId)
        .select()
        .single();

      if (error) throw error;

      console.log('✅ Message archived:', messageId);
      return data;
    } catch (error) {
      console.error('❌ Error archiving message:', error);
      throw error;
    }
  },

  // Bulk operations
  async broadcastMessage(messageData, senderId, recipientIds = null) {
    console.log('📢 Broadcasting message to all family members');
    
    try {
      // Get all family members if no specific recipients
      if (!recipientIds) {
        const { data: members, error: membersError } = await supabase
          .from('family_members')
          .select('id')
          .neq('id', senderId); // Don't send to self

        if (membersError) throw membersError;
        recipientIds = members.map(m => m.id);
      }

      const broadcastData = {
        ...messageData,
        type: 'family_broadcast',
        recipientIds
      };

      return await this.sendMessage(broadcastData, senderId);
    } catch (error) {
      console.error('❌ Error broadcasting message:', error);
      throw error;
    }
  },

  async markMultipleAsRead(messageIds, readerId) {
    console.log('👁️ Marking multiple messages as read:', messageIds.length);
    
    try {
      const results = await Promise.all(
        messageIds.map(id => this.markAsRead(id, readerId))
      );

      console.log('✅ Multiple messages marked as read');
      return results;
    } catch (error) {
      console.error('❌ Error marking multiple messages as read:', error);
      throw error;
    }
  },

  async deleteMultipleMessages(messageIds, deleterId) {
    console.log('🗑️ Deleting multiple messages:', messageIds.length);
    
    try {
      const { error } = await supabase
        .from('family_messages')
        .delete()
        .in('id', messageIds)
        .eq('sender_id', deleterId);

      if (error) throw error;

      console.log('✅ Multiple messages deleted');
      return { success: true, count: messageIds.length };
    } catch (error) {
      console.error('❌ Error deleting multiple messages:', error);
      throw error;
    }
  },

  // Personal Notes operations
  async getPersonalNotes(ownerId, filters = {}) {
    console.log('📝 Getting personal notes for:', ownerId, 'with filters:', filters);
    
    try {
      let query = supabase
        .from('personal_notes')
        .select('*')
        .eq('owner_id', ownerId);

      if (filters.category) {
        query = query.eq('category', filters.category);
      }
      if (filters.isPinned) {
        query = query.eq('is_pinned', filters.isPinned);
      }
      if (filters.hasReminder) {
        query = query.not('reminder_date', 'is', null);
      }

      query = query.order('is_pinned', { ascending: false })
                  .order('updated_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      console.log('✅ Personal notes retrieved:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('❌ Error getting personal notes:', error);
      throw error;
    }
  },

  async createPersonalNote(ownerId, noteData) {
    console.log('📝 Creating personal note for:', ownerId);
    
    try {
      const { data, error } = await supabase
        .from('personal_notes')
        .insert({
          owner_id: ownerId,
          title: noteData.title,
          content: noteData.content,
          category: noteData.category || 'general',
          is_pinned: noteData.isPinned || false,
          reminder_date: noteData.reminderDate || null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      console.log('✅ Personal note created:', data?.id);
      return data;
    } catch (error) {
      console.error('❌ Error creating personal note:', error);
      throw error;
    }
  },

  async updatePersonalNote(noteId, updates, ownerId) {
    console.log('✏️ Updating personal note:', noteId);
    
    try {
      const updateData = {
        updated_at: new Date().toISOString()
      };

      if (updates.title !== undefined) updateData.title = updates.title;
      if (updates.content !== undefined) updateData.content = updates.content;
      if (updates.category !== undefined) updateData.category = updates.category;
      if (updates.isPinned !== undefined) updateData.is_pinned = updates.isPinned;
      if (updates.reminderDate !== undefined) updateData.reminder_date = updates.reminderDate;

      const { data, error } = await supabase
        .from('personal_notes')
        .update(updateData)
        .eq('id', noteId)
        .eq('owner_id', ownerId) // Ensure user can only update their own notes
        .select()
        .single();

      if (error) throw error;
      if (!data) throw new Error('Note not found or unauthorized');

      console.log('✅ Personal note updated:', noteId);
      return data;
    } catch (error) {
      console.error('❌ Error updating personal note:', error);
      throw error;
    }
  },

  async deletePersonalNote(noteId, ownerId) {
    console.log('🗑️ Deleting personal note:', noteId);
    
    try {
      const { error } = await supabase
        .from('personal_notes')
        .delete()
        .eq('id', noteId)
        .eq('owner_id', ownerId); // Ensure user can only delete their own notes

      if (error) throw error;

      console.log('✅ Personal note deleted:', noteId);
      return { success: true };
    } catch (error) {
      console.error('❌ Error deleting personal note:', error);
      throw error;
    }
  },

  // Search and filtering
  async searchMessages(searchTerm, currentUserId, filters = {}) {
    console.log('🔍 Searching messages:', searchTerm);
    
    try {
      let query = supabase
        .from('family_messages')
        .select(`
          *,
          sender:family_members!sender_id(id, name, avatar, role),
          recipients:message_recipients(
            *,
            recipient:family_members!recipient_id(id, name, avatar, role)
          )
        `)
        .or(`subject.ilike.%${searchTerm}%,content.ilike.%${searchTerm}%`);

      // Apply additional filters
      if (filters.type) query = query.eq('type', filters.type);
      if (filters.priority) query = query.eq('priority', filters.priority);
      if (filters.status) query = query.eq('status', filters.status);

      // Ensure user has access to the messages
      const { data: userMessages } = await supabase
        .from('message_recipients')
        .select('message_id')
        .eq('recipient_id', currentUserId);
      
      const userMessageIds = userMessages?.map(m => m.message_id) || [];
      
      // Filter to messages user sent or received
      query = query.or(`sender_id.eq.${currentUserId}${
        userMessageIds.length > 0 ? `,id.in.(${userMessageIds.join(',')})` : ''
      }`);

      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      console.log('✅ Search results:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('❌ Error searching messages:', error);
      throw error;
    }
  },

  async getUnreadCount(userId) {
    console.log('📊 Getting unread count for user:', userId);
    
    try {
      // Count unread recipients for this user
      const { count, error } = await supabase
        .from('message_recipients')
        .select('id', { count: 'exact' })
        .eq('recipient_id', userId)
        .eq('status', 'unread');

      if (error) throw error;

      console.log('✅ Unread count:', count);
      return count || 0;
    } catch (error) {
      console.error('❌ Error getting unread count:', error);
      throw error;
    }
  },

  async getMessagesByStatus(userId, status) {
    console.log('📊 Getting messages by status:', status);
    
    try {
      const { data, error } = await supabase
        .from('family_messages')
        .select(`
          *,
          sender:family_members!sender_id(id, name, avatar, role),
          recipients:message_recipients!inner(
            *,
            recipient:family_members!recipient_id(id, name, avatar, role)
          )
        `)
        .eq('recipients.recipient_id', userId)
        .eq('recipients.status', status)
        .order('created_at', { ascending: false });

      if (error) throw error;

      console.log('✅ Messages by status retrieved:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('❌ Error getting messages by status:', error);
      throw error;
    }
  },

  // Integration helpers
  async createSystemMessage(messageData) {
    console.log('🤖 Creating system message');
    
    try {
      // System messages don't need a real sender, use a system user ID or first parent
      const { data: systemSender } = await supabase
        .from('family_members')
        .select('id')
        .eq('role', 'parent')
        .limit(1)
        .single();

      const systemMessageData = {
        ...messageData,
        type: 'system',
        priority: messageData.priority || 'normal'
      };

      return await this.sendMessage(systemMessageData, systemSender.id);
    } catch (error) {
      console.error('❌ Error creating system message:', error);
      throw error;
    }
  },

  // Real-time subscriptions
  subscribeToMessages(userId, callback) {
    console.log('🔔 Subscribing to messages for user:', userId);
    
    // Add a timestamp to make channel name unique per subscription
    const channelId = `messages-${userId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const subscription = supabase
      .channel(channelId)
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'family_messages'
        }, 
        (payload) => {
          console.log('🔄 Message change:', payload);
          callback(payload);
        }
      )
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'message_recipients',
          filter: `recipient_id=eq.${userId}`
        }, 
        (payload) => {
          console.log('🔄 Message recipient change:', payload);
          callback(payload);
        }
      )
      .subscribe();
    
    return subscription;
  },

  subscribeToPersonalNotes(ownerId, callback) {
    console.log('🔔 Subscribing to personal notes for:', ownerId);
    
    // Add a timestamp to make channel name unique per subscription
    const channelId = `notes-${ownerId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const subscription = supabase
      .channel(channelId)
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'personal_notes',
          filter: `owner_id=eq.${ownerId}`
        }, 
        (payload) => {
          console.log('🔄 Personal note change:', payload);
          callback(payload);
        }
      )
      .subscribe();
    
    return subscription;
  },

  unsubscribe(subscription) {
    if (subscription) {
      supabase.removeChannel(subscription);
    }
  },

  // Utility functions
  _validateMessageType(type, senderRole) {
    const validTypes = {
      parent: ['from_parent', 'family_broadcast', 'system', 'reply', 'personal_note', 'reminder'],
      child: ['to_parent', 'reply', 'personal_note', 'reminder']
    };

    if (!validTypes[senderRole]?.includes(type)) {
      throw new Error(`Invalid message type '${type}' for role '${senderRole}'`);
    }
  },

  async _addAttachments(messageId, attachments, uploaderId) {
    console.log('📎 Adding attachments to message:', messageId);
    
    try {
      const attachmentData = attachments.map(attachment => ({
        message_id: messageId,
        file_name: attachment.fileName,
        file_type: attachment.fileType,
        file_size: attachment.fileSize,
        storage_path: attachment.storagePath,
        thumbnail_path: attachment.thumbnailPath,
        uploaded_by: uploaderId
      }));

      const { data, error } = await supabase
        .from('message_attachments')
        .insert(attachmentData)
        .select();

      if (error) throw error;

      console.log('✅ Attachments added:', data?.length || 0);
      return data;
    } catch (error) {
      console.error('❌ Error adding attachments:', error);
      throw error;
    }
  },

  // Message templates for common scenarios
  getMessageTemplates(senderRole) {
    const templates = {
      parent: [
        {
          type: 'from_parent',
          subject: 'Daily Reminder',
          content: 'Don\'t forget to...',
          priority: 'normal'
        },
        {
          type: 'from_parent',
          subject: 'Urgent: Permission Needed',
          content: 'I need your response about...',
          priority: 'urgent'
        },
        {
          type: 'family_broadcast',
          subject: 'Family Announcement',
          content: 'Everyone please note...',
          priority: 'normal'
        }
      ],
      child: [
        {
          type: 'to_parent',
          subject: 'Permission Request',
          content: 'Can I please...',
          priority: 'normal'
        },
        {
          type: 'to_parent',
          subject: 'Help Needed',
          content: 'I need help with...',
          priority: 'normal'
        },
        {
          type: 'to_parent',
          subject: 'Question',
          content: 'I have a question about...',
          priority: 'normal'
        }
      ]
    };

    return templates[senderRole] || [];
  }
};